import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Navigation, MapPin, Clock, Phone, User } from 'lucide-react';
import { TrackingMap } from './TrackingMap';
import { LocationControls } from './LocationControls';
import { supabase } from '@/integrations/supabase/client';
import { format, formatDistanceToNow, differenceInMinutes } from 'date-fns';

import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { toast } from 'sonner';
import { LocationTracker, type LocationData, setLocationSharingForJob } from '@/utils/locationTracker';

interface TradesmanLocation {
  latitude: number;
  longitude: number;
  accuracy?: number;
  last_updated: string;
  tradesman_name: string;
}

interface Job {
  id: string;
  title: string;
  location: string;
  status: string;
  customer_id?: string;
  tradesman_id?: string;
  required_date?: string;
}

interface JobTrackingDialogProps {
  job: Job | null;
  onClose: () => void;
}

export const JobTrackingDialog: React.FC<JobTrackingDialogProps> = ({ job, onClose }) => {
  const [tradesmanLocation, setTradesmanLocation] = useState<TradesmanLocation | null>(null);
  const [customerLocation, setCustomerLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [customerLiveLocation, setCustomerLiveLocation] = useState<TradesmanLocation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tradesmanPhone, setTradesmanPhone] = useState<string | null>(null);

  // Force periodic re-render so relative times stay fresh
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (!job || !job.tradesman_id) {
      console.log('JobTrackingDialog: No job or tradesman ID provided');
      return;
    }

    console.log('JobTrackingDialog: Starting location fetch for job:', job.id, 'tradesman:', job.tradesman_id);

    const fetchLocations = async () => {
      try {
        setIsLoading(true);
        
        // Fetch tradesman phone number
        const { data: tradesmanProfile, error: tradesmanError } = await supabase
          .from('profiles')
          .select('phone')
          .eq('id', job.tradesman_id)
          .maybeSingle();

        if (tradesmanProfile && !tradesmanError) {
          setTradesmanPhone(tradesmanProfile.phone);
        }

        // Fetch tradesman live location via secure RPC
        const { data: tLoc, error: tLocErr } = await supabase.rpc('get_tradesman_live_location_for_job', { p_job_id: job.id });
        if (tLocErr) {
          console.log('JobTrackingDialog: Tradesman location RPC error:', tLocErr.message);
        }
        const tLocRow = Array.isArray(tLoc) ? tLoc[0] : tLoc;

        if (tLocRow) {
          setTradesmanLocation({
            latitude: Number(tLocRow.latitude),
            longitude: Number(tLocRow.longitude),
            accuracy: tLocRow.accuracy ? Number(tLocRow.accuracy) : undefined,
            last_updated: tLocRow.last_updated,
            tradesman_name: 'Tradesman',
          });
        } else {
          console.log('JobTrackingDialog: No tradesman location returned by RPC');
        }

        // Fetch customer location sharing for this job via secure RPC
        console.log('JobTrackingDialog: Fetching customer location sharing...');
        const { data: sharedLoc, error: sharedErr } = await supabase.rpc('get_shared_job_location', { p_job_id: job.id });
        if (sharedErr) {
          console.log('JobTrackingDialog: Shared job location RPC error:', sharedErr.message);
        }
        const sharedRow = Array.isArray(sharedLoc) ? sharedLoc[0] : sharedLoc;

        if (sharedRow && sharedRow.customer_latitude && sharedRow.customer_longitude) {
          console.log('JobTrackingDialog: Setting customer location from sharing');
          const lat = Number(sharedRow.customer_latitude);
          const lng = Number(sharedRow.customer_longitude);
          setCustomerLocation({
            latitude: lat,
            longitude: lng,
          });
          // Also reflect this as the customer's live/job location so the customer sees their own pin even if user_locations isn't available
          setCustomerLiveLocation({
            latitude: lat,
            longitude: lng,
            last_updated: sharedRow.updated_at,
            tradesman_name: 'Your Location',
          });
        } else {
          console.log('JobTrackingDialog: No customer location sharing found');
        }

        // Fetch customer's own live location via secure RPC
        console.log('JobTrackingDialog: Fetching customer live location (RPC)...');
        const { data: cLoc, error: cLocErr } = await supabase.rpc('get_customer_live_location_for_job', { p_job_id: job.id });
        if (cLocErr) {
          console.log('JobTrackingDialog: Customer location RPC error:', cLocErr.message);
        }
        const cRow = Array.isArray(cLoc) ? cLoc[0] : cLoc;

        if (cRow) {
          console.log('JobTrackingDialog: Setting customer live location from RPC');
          setCustomerLiveLocation({
            latitude: Number(cRow.latitude),
            longitude: Number(cRow.longitude),
            accuracy: cRow.accuracy ? Number(cRow.accuracy) : undefined,
            last_updated: cRow.last_updated,
            tradesman_name: 'Your Location',
          });
        }
      } catch (error) {
        console.error('JobTrackingDialog: Error fetching locations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocations();

    // Set up polling for location updates (every 30 seconds)
    const pollInterval = setInterval(async () => {
      try {
        // Poll tradesman location
        const { data: tLoc } = await supabase.rpc('get_tradesman_live_location_for_job', { p_job_id: job.id });
        const tLocRow = Array.isArray(tLoc) ? tLoc[0] : tLoc;
        
        if (tLocRow) {
          setTradesmanLocation({
            latitude: Number(tLocRow.latitude),
            longitude: Number(tLocRow.longitude),
            accuracy: tLocRow.accuracy ? Number(tLocRow.accuracy) : undefined,
            last_updated: tLocRow.last_updated,
            tradesman_name: 'Tradesman',
          });
        }
        
        // Poll customer shared location
        const { data: sharedLoc } = await supabase.rpc('get_shared_job_location', { p_job_id: job.id });
        const sharedRow = Array.isArray(sharedLoc) ? sharedLoc[0] : sharedLoc;
        
        if (sharedRow && sharedRow.customer_latitude && sharedRow.customer_longitude) {
          const lat = Number(sharedRow.customer_latitude);
          const lng = Number(sharedRow.customer_longitude);
          setCustomerLocation({ latitude: lat, longitude: lng });
          setCustomerLiveLocation({
            latitude: lat,
            longitude: lng,
            last_updated: sharedRow.updated_at,
            tradesman_name: 'Your Location',
          });
        }
        
        // Poll customer live location
        const { data: cLoc } = await supabase.rpc('get_customer_live_location_for_job', { p_job_id: job.id });
        const cRow = Array.isArray(cLoc) ? cLoc[0] : cLoc;
        
        if (cRow) {
          setCustomerLiveLocation({
            latitude: Number(cRow.latitude),
            longitude: Number(cRow.longitude),
            accuracy: cRow.accuracy ? Number(cRow.accuracy) : undefined,
            last_updated: cRow.last_updated,
            tradesman_name: 'Your Location',
          });
        }
      } catch (error) {
        console.error('JobTrackingDialog: Error polling locations:', error);
      }
    }, 30000);

    return () => {
      console.log('JobTrackingDialog: Cleaning up polling interval');
      clearInterval(pollInterval);
    };
  }, [job]);

  // Auto-start lightweight tracking for customers to continuously share job location
  useEffect(() => {
    if (!job) return;
    
    const tracker = new LocationTracker();
    let hasSharedInitialLocation = false;
    
    const shareLocation = async (loc: LocationData) => {
      // Reflect live location locally
      setCustomerLiveLocation({
        latitude: loc.latitude,
        longitude: loc.longitude,
        accuracy: loc.accuracy,
        last_updated: new Date().toISOString(),
        tradesman_name: 'Your Location',
      });
      setCustomerLocation({ latitude: loc.latitude, longitude: loc.longitude });
      
      // Share with tradesman immediately
      try {
        console.log('JobTrackingDialog: Sharing customer location with tradesman...', loc);
        await setLocationSharingForJob(job.id, loc, job.location);
        console.log('JobTrackingDialog: Successfully shared customer location');
        hasSharedInitialLocation = true;
      } catch (err) {
        console.error('JobTrackingDialog: setLocationSharingForJob error', err);
      }
    };
    
    (async () => {
      try {
        console.log('JobTrackingDialog: Starting customer location tracking for job:', job.id);
        await tracker.startTracking(shareLocation);
      } catch (e) {
        console.error('JobTrackingDialog: Auto-tracking denied/failed', e);
        toast.error('Location sharing requires permission. Please enable location access.');
      }
    })();

    return () => {
      console.log('JobTrackingDialog: Stopping customer location tracking');
      tracker.stopTracking();
    };
  }, [job]);

  const handleCallTradesman = async () => {
    if (!tradesmanPhone) {
      toast.error('Tradesman phone number not available');
      return;
    }

    try {
      if (Capacitor.isNativePlatform()) {
        // For mobile app, use native phone dialer
        const info = await Device.getInfo();
        if (info.platform === 'ios' || info.platform === 'android') {
          // Open phone dialer with the tradesman's number
          window.open(`tel:${tradesmanPhone}`, '_self');
        }
      } else {
        // For web, open phone dialer
        window.open(`tel:${tradesmanPhone}`, '_self');
      }
    } catch (error) {
      console.error('Error making phone call:', error);
      toast.error('Unable to make phone call');
    }
  };

  if (!job) return null;

  const mapLocations = [
    ...(tradesmanLocation ? [{
      latitude: tradesmanLocation.latitude,
      longitude: tradesmanLocation.longitude,
      accuracy: tradesmanLocation.accuracy,
      title: tradesmanLocation.tradesman_name,
      description: `Updated ${formatDistanceToNow(new Date(tradesmanLocation.last_updated), { addSuffix: true })}`,
      type: 'tradesman' as const,
    }] : []),
    ...(customerLiveLocation ? [{
      latitude: customerLiveLocation.latitude,
      longitude: customerLiveLocation.longitude,
      accuracy: customerLiveLocation.accuracy,
      title: 'Your Location',
      description: `Updated ${formatDistanceToNow(new Date(customerLiveLocation.last_updated), { addSuffix: true })}`,
      type: 'customer' as const,
    }] : []),
  ];

  const isTradesmanStale = tradesmanLocation ? differenceInMinutes(new Date(), new Date(tradesmanLocation.last_updated)) > 5 : false;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={!!job} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Navigation className="h-5 w-5" />
            Track Tradesman Progress
          </DialogTitle>
          <DialogDescription>
            Real-time location tracking for your job: {job.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Job Info */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <h3 className="font-semibold">{job.title}</h3>
              <div className="flex items-center gap-1 text-muted-foreground text-sm">
                <MapPin className="h-4 w-4" />
                <span>{job.location}</span>
              </div>
              {job.required_date && (
                <div className="flex items-center gap-1 text-muted-foreground text-sm">
                  <Clock className="h-4 w-4" />
                  <span>Scheduled: {format(new Date(job.required_date), 'PPP p')}</span>
                </div>
              )}
            </div>
            <Badge className={getStatusColor(job.status)} variant="secondary">
              {job.status.replace('_', ' ')}
            </Badge>
          </div>

          {/* Customer Location Controls */}
          <LocationControls
            userType="customer"
            activeJobId={job.id}
            onLocationUpdate={(loc) =>
              setCustomerLiveLocation({
                latitude: loc.latitude,
                longitude: loc.longitude,
                accuracy: loc.accuracy,
                last_updated: new Date().toISOString(),
                tradesman_name: 'Your Location',
              })
            }
          />

          {/* Tracking Status */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-accent-orange" />
                <span className="font-medium">Tradesman Status</span>
              </div>
              {tradesmanLocation ? (
                <div className="space-y-1 text-sm">
                  <div className={isTradesmanStale ? 'text-amber-600 font-medium' : 'text-green-600 font-medium'}>
                    {isTradesmanStale ? '⚠️ Location Inactive' : '📍 Location Active'}
                  </div>
                  <div className="text-muted-foreground">
                    Last update: {formatDistanceToNow(new Date(tradesmanLocation.last_updated), { addSuffix: true })}
                  </div>
                  {tradesmanLocation.accuracy && (
                    <div className="text-muted-foreground">
                      Accuracy: ±{Math.round(tradesmanLocation.accuracy)}m
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-1 text-sm">
                  <div className="text-amber-600 font-medium">⚠️ Location Not Shared</div>
                  <div className="text-muted-foreground">
                    The tradesman needs to enable location tracking to appear on the map
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 p-2 bg-amber-50 rounded border">
                    💡 <strong>Note:</strong> Ask your tradesman to open their job navigation and enable location sharing
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="font-medium">Your Live Location</span>
              </div>
              {(customerLiveLocation || customerLocation) ? (
                <div className="text-sm text-green-600 font-medium">
                  Location Shared
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Location not shared
                </div>
              )}
            </div>
          </div>

          {/* Map */}
          <div className="space-y-2">
            <h4 className="font-medium">Live Tracking Map</h4>
            {isLoading ? (
              <div className="h-80 bg-muted rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading map...</p>
                </div>
              </div>
            ) : (
              <TrackingMap
                locations={mapLocations}
                className="h-80 w-full border rounded-lg"
                centerLat={mapLocations[0]?.latitude}
                centerLng={mapLocations[0]?.longitude}
                zoom={14}
                interactive={true}
                showNavigation={true}
                fitToLocations={mapLocations.length > 1}
              />
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button onClick={onClose} variant="outline" className="flex-1">
              Close Tracking
            </Button>
            {tradesmanLocation && tradesmanPhone && (
              <Button onClick={handleCallTradesman} variant="hero" className="flex-1">
                <Phone className="h-4 w-4 mr-2" />
                Call Tradesman
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};